import React, { memo, useEffect } from 'react';

import { ExperienceSuccessTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/result-declared/index.tsx';
import { ExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * PageContainerSuccessTracker is a copy of the old NavigationSuccessTracker component from this path:
 * jira/src/packages/navigation-apps/atlassian-navigation/src/controllers/navigation-success-tracker/main.tsx
 * Which used to mount on every page. But now it doesn't we aren't getting page container success events.
 *
 * So we are mounting ExperienceSuccessTracker here just so the parent event fires and our SLO works again.
 * See this thread for more info: https://atlassian.slack.com/archives/C075QTJBJ6S/p1752445800976529
 *
 * Not sure if ExperienceSuccess is explicitly required here, but it was in the old component, so we are keeping it for now.
 */
function PageContainerSuccessTrackerInner() {
	return (
		<JSErrorBoundary
			id="pageContainerSuccessTracker"
			packageName="jiraPageContainerV2"
			teamName="jfp-magma"
			fallback="unmount"
		>
			<ExperienceSuccessTracker
				location="pageContainer"
				parentProviders={null}
				failureEventAttributes={null}
			>
				<ExperienceSuccess experience="pageContainer" />
			</ExperienceSuccessTracker>
			{fg('jfp-magma-is-static-fallback-experience-attribute') && <StaticFallbackSuccessTracker />}
		</JSErrorBoundary>
	);
}

declare global {
	interface Window {
		__SSR_FALLBACK__?: boolean;
	}
}

/**
 * Emits an event so that we know SSR static fallback rendering has reached as far as React rendering, and not crashed
 * at some earlier point.
 */
const StaticFallbackSuccessTracker = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (typeof window !== 'undefined' && window.__SSR_FALLBACK__ === true) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'staticFallback rendered');
		}
	}, [createAnalyticsEvent]);

	return null;
};

export const PageContainerSuccessTracker = memo(PageContainerSuccessTrackerInner);
